/**
 * Waffle flags should be added to this list before usage in JS/TS.
 * Each entry should:
 *   - Be given a descriptive key name
 *   - Include a description of what the flag gates
 *   - Link to a ticket for flag removal, or an explanation as to why there isn't one
 *
 * Note: Though this enum can (and should!) be used from JS/Ractive, its usage is only
 * _enforced_ in TS code, so it's still recommended to search for both the enum AND the
 * "raw" waffle flag string for removal, unless you are sure you know otherwise.
 */
enum WaffleFlag {
  // Old flags without owners
  // ------------------------
  // Enables an emoji picker add-on to the composer (no removal ticket known)
  EmojiPicker = 'emoji-picker-widget',
  // Allows us to set a base URL for static lists API that is on a different
  // domain (WEB-888)
  StaticListsCORS = 'static-lists-cors-flag',
  // Controls access to the SFMC integrations, SFMC AppExchange and RTDS. Used by Bizops for contracted access so don't remove.
  CONNECT_SFMC_FLAG = 'connect-sfmc-flag',

  // ADVSEG flags
  // ------------
  // Enable Channel coordination in the builder (removal : ADVSEG-1027)
  BuilderChannelCoordination = 'builder-channel-coordination-flag',
  // Enable Push local time for A/B testing (removal : ADVSEG-1815)
  PushLocalTimeForExperiment = 'push-local-time-for-experiment-flag',
  // Enable throttling for A/B testing (removal : ADVSEG-1894)
  ThrottlingForExperiment = 'throttling-for-experiment-flag',
  // EEnable new design for the global create popup (removal : ADVSEG-1968)
  GlobalCreateReworkFlag = 'global-create-rework-flag',
  // Enable NEW ACTIONS IN  WYSIWYG (removal : ADVSEG-2272)
  ExtraActionsInWYSIWYG = 'extra-actions-in-wysiwyg-flag',
  // Enable contact checkbox in feedback modal (removal: ADVSEG-2362)
  ContactCheckBoxFeedbackFlag = 'contact-checkbox-feedback-flag',
  // Add experiment data to survey aggregate reports (removal: ADVSEG-4620)
  SurveysInScenesAggregateReport = 'surveys-in-scenes-aggregate-report',
  // Enables Tag Group validation for segments (Removal: ADVSEG-5014)
  SegmentTagGroupValidation = 'segment-tag-group-validation-flag',
  // Enables the use of the legavy surveys (Removal: ADVSEG-4740)
  LegacySurveyMigration = 'legacy_survey_migration',
  // Enables new Iaa debugger in the control panels
  IaaDebugger = 'iaa-debugger',
  // Enabled video on background in stories (Removal: ADVSEG-5369)
  StoryBackgroundVideo = 'story-background-video',
  // Separated Settings for IAA & IAE (Removal-ADVSEG-5390)
  IAESettings = 'iae-settings',
  // Enabled new FCM certificate (Removal-ADVSEG-5431)
  FcmHttpV1 = 'fcm-http-v1',
  // Enable ability to optionally set the click behavior for button action in scenes (Removal: ADVSEG-5504)
  ScenesClickBehavior = 'scenes-click-behavior',
  // Enables the v2 of properties tab for scenes (removal: ADVSEG-5554)
  ScenesPropertiesV2 = 'scenes-properties-v2',
  // Adds Scale styles to properties tab for scenes (removal: ADVSEG-5710)
  SceneScaleProperties = 'scene-scale-properties',
  // Enables fixing text items bellow buttons for scenes (removal: ADVSEG-5712)
  SceneFixedText = 'scene-fixed-text',
  // Enables the scenes behavior for the Text item actions (removal: ADVSEG-5748)
  TextActionClickBehaviour = 'text-action-click-behaviour',
  // Enabled Embedded option for scene (removal: ADVSEG-5770)
  AxpEmbedded = 'axp-embedded',
  // Enable edited component indicator preview (removal: ADVSEG-5714)
  EditedItemPreviewIndicator = 'edited-item-preview-indicator',
  // Hide delete while idle android feature from ui (removal: ADVSEG-5822)
  RemoveDelayWhileIdle = 'remove-delay-while-idle',
  // Adds the Scene border radius input to the properties tab (removal: ADVSEG-5825)
  SceneBorderRadius = 'scene-border-radius',

  // CHAN flags
  // ----------
  // Enables Generic pass type for google wallet (removal: CHAN-4603)
  GoogleGeneric = 'google-generic-flag',
  // Enable Google Template Overrides for Loyalty Passes (removal: CHAN-4429)
  LoyaltyOverrides = 'google-overrides-loyalty-flag',
  // Enable template duplication in wallet dashboard (removal: CHAN-5131)
  WalletTemplateDuplication = 'wallet-template-duplication-flag',
  // SMS Retargeting (Removal: CHAN-3806)
  SMSRetargeting = 'sms-retargeting',
  // New app settings dashboard
  ControlPanelDarkMatterSms = 'control-panel-dark-matter-sms',
  // Bulk add/remove tags (removal: CHAN-4392)
  ContactManagementTagUpload = 'contact-management-tag-upload-flag',
  // Contact Management Named User messages embedded looker sso url
  // (removal CHAN-4620)
  NamedUserMessagesDemo = 'named-user-messages-demo-flag',
  // Adding email as a localizable platform (removal: CHAN-5077)
  EmailLocalization = 'email-localization',
  // EXP flags
  // ---------
  // The Feature Flags feature as a whole. Currently in "special access", rolling out to select customers
  // (Removal: EXP-62)
  FeatureFlags = 'feature-flags',
  // The Feature Flags "Reports" page
  // (Removal: EXP-156)
  FeatureFlagReports = 'feature-flag-reports',
  // Show internal dropdown to configure a flag's TTL
  FeatureFlagCacheConfig = 'feature-flag-cache-config',
  // The Experimentation Hub. deployment on hold.
  // (Removal: EXP-102)
  ExperimentsOverview = 'experiments-overview',
  // Ability to target feature flag in IAA
  FeatureFlagIaxAudience = 'feature-flag-iax-audience',

  // ORC flags
  // ---------
  // Generated reactivation exits for reactivation journeys (ORC-2344)
  GeneratedReactivationCancellationExits = 'generated-reactivation-cancellation',
  // Enables doing A/B tests on delays between messages in journeys. (removal: ORC-2679)
  JourneyABTestDelay = 'journey-ab-test-delay',
  // Enables new enhanced display of sequence reporting (removal: ORC-3990)
  SequenceReportingQ32022 = 'sequence-reporting-q3-2022',
  // Enables Journey Map Drawer Improvements (Removal: ORC-4593)
  JourneyMapDrawerImprovements = 'journey-map-drawer-improvements',
  // Replaces the old options component with the new design system one (Removal: ORC-4836)
  DesignSystemOptionsComponent = 'design-system-options-component',
  // Add OAuth support (removal: ORC-5254)
  OAuthSupport = 'oauth-support',
  // Removes the outcomes tab in the sequence configuration page (removal: ORC-5272)
  OutcomesTabRemoval = 'outcomes-tab-removal',

  // PINT flags
  // ----------
  // SFTP Configuration in APIs & Integrations
  SftpConfiguration = 'sftp-configuration-flag',
  // Enbables Named User Association list upload (removal: PINT-1575)
  NamedUserAssociationListUpload = 'named-user-association-list-upload',
  // Enables the new GA4 integration and hides the old (removal: PINT-1861)
  GoogleAnalytics4 = 'google-analytics-4',
  // Enables early access to Movable Ink integration (removal: PINT-1960)
  MovableInk = 'movable-ink',

  // TPS flags
  // ---------
  // Hides legacy rich push editor unless an app has custom Rich Push templates. (removal: TPS-689)
  HideLegacyRichPushEditorUnlessAppHasCustomTemplates = 'hide-legacy-rich-push-editor',
  // Enables cross-channel segmentation UI additionsa (removal: TPS-3257)
  CrossChannelSegmentation = 'cross-channel-segmentation',
  // Enables the subscription list reports page (Removal: TPS-3764)
  SubscriptionListReports = 'subscription-list-reports',
  // Enables advanced Goal report filters (removal: TPS-4339)
  GoalReportFilters = 'goal-report-filters',
  // Enables impressions billing page (removal: TPS-4451)
  ImpressionsBilling = 'impressions-billing',
  // Enables contract data on the impressions billing page (removal: TPS-4451)
  ImpressionsBillingContractData = 'impressions-billing-contract-data',
  // Enabled Recharts visualization for subscription list reports (removal: TPS-4480)
  SubscriptionListVisualization = 'subscription-list-visualization',
  // Enables the new reports pages (removal: TPS-4041)
  NextReportsUI = 'next-reports-ui',
  // Enables reporting hub v1 features (removal: TPS-4576)
  ReportingHubV1 = 'reporting-hub-v1',
  // Enables JSON attributes (removal: TPS-4668)
  JSONAttributes = 'json-attributes',

  // WPLAT flags
  // -----------
  // Disables operator selection for static lists in the segment builder (WPLAT-711)
  SegmentListOperatorDisabled = 'segment-list-operator-disabled',
  // Enables existing users to see and/or experience AXP online (removal: WPLAT-770)
  AxpForExistingUsers = 'axp-for-existing-users',
  // Enables the new axp ecomm flow (removal: WPLAT-770)
  AxpEcommV1 = 'axp-ecomm-v1',

  // TOOLS flags
  // -----------
  // Features for flight deck 1.0 release (Removal: TOOLSLIBS-1402)
  FD10 = 'fd-10-flag',

  // DS flags
  // -----------
  // Changes DM Switch pattern so that it simply renders the DS Switch (Removal: DS-228)
  SwitchSwitch = 'switch-switch',

  // ^^^ Add new flags to your team's section ^^^
  // Be sure to add new flags to airship/polybius/flags.py, otherwise they
  // will not be created by the sync_flags management command!
}

export default WaffleFlag
